import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import type { AvatarKey } from '@hazel/shared'

import { api, ApiError } from '../api/client.js'
import { useSession } from '../auth/session-context.js'
import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { Input } from '../components/Input.js'

interface RecentProfile {
  id: string
  username: string
  displayName: string
  avatarKey: AvatarKey | null
}

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Where she was going before being asked to log in.
   *
   * Only a path from this app: a redirect target taken from wherever the
   * browser happens to have been is how an open redirect gets built by
   * accident.
   */
  const from = (location.state as { from?: string } | null)?.from
  const goingTo = typeof from === 'string' && from.startsWith('/') ? from : '/'
  const { refresh } = useSession()

  const [recent, setRecent] = useState<RecentProfile[]>([])
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void api<RecentProfile[]>('/api/auth/recent')
      .then(setRecent)
      .catch(() => setRecent([]))
  }, [])

  /**
   * Tapping a teddy fills the username and moves to the password.
   *
   * It never logs anyone in on its own — the password is still required every
   * time. This is a convenience for a child on a phone keyboard, not a way in.
   */
  function chooseProfile(profile: RecentProfile): void {
    setUsername(profile.username)
    setError(null)
    passwordRef.current?.focus()
  }

  async function submit(): Promise<void> {
    setError(null)
    setBusy(true)

    try {
      await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      })

      await refresh()
      await navigate(goingTo)
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many tries. Have a little break and try again soon.'
          : "That username and password didn't match.",
      )
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  async function forgetEveryone(): Promise<void> {
    await api('/api/auth/recent', { method: 'DELETE' }).catch(() => undefined)
    setRecent([])
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-5">
      <h1 className="text-(length:--text-hero)">Hello again</h1>

      {recent.length > 0 ? (
        <Card>
          <h2 className="text-(length:--text-heading) mb-3">Is this you?</h2>

          <ul className="flex list-none flex-wrap gap-3 p-0">
            {recent.map((profile) => (
              <li key={profile.id}>
                <button
                  type="button"
                  onClick={() => chooseProfile(profile)}
                  className="hover:bg-surface-sunken flex min-h-(--size-tap) flex-col items-center gap-1 rounded-(--radius-control) p-2"
                >
                  <Avatar teddy={profile.avatarKey} name={profile.displayName} size="medium" />
                  <span className="font-display text-(length:--text-small)">
                    {profile.displayName}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => void forgetEveryone()}
            className="text-ink-muted text-(length:--text-small) mt-3 underline"
          >
            Not you? Forget these
          </button>
        </Card>
      ) : null}

      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
          className="flex flex-col gap-4"
        >
          <Input
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />

          <Input
            ref={passwordRef}
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            {...(error ? { error } : {})}
          />

          <Button type="submit" block disabled={busy}>
            {busy ? 'Just a moment…' : 'Play'}
          </Button>
        </form>
      </Card>

      {/*
        The other door.

        Somebody handed a phone and a four-letter code has no account and does
        not want one — and without this the login screen is a form they cannot
        fill in and nothing else. A room is the one place this app is generous
        about who is playing.
      */}
      <Card>
        <h2 className="text-(length:--text-heading) mb-1">Been given a code?</h2>
        <p className="text-ink-muted text-(length:--text-small) mb-3">
          If somebody has a game up on the telly, you can join it from here. You don&rsquo;t need an
          account.
        </p>

        <Link
          to="/join"
          className="border-border hover:bg-surface-sunken flex min-h-(--size-tap) items-center justify-center rounded-(--radius-control) border-2 px-4 font-display"
        >
          Join a game
        </Link>
      </Card>
    </div>
  )
}
